import React from 'react';
import { Box, Typography, Card, CardContent, Button, Stack, Chip, Avatar } from '@mui/material';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded'; // 공지사항 주석 처리로 미사용
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';

const Dashboard = ({ user, onNavigate }) => {
  const externalLinks = [
    {
      title: '한국수어원 네이버 카페',
      desc: '회원 간의 정보 공유 및 커뮤니티 공간',
      icon: <ForumRoundedIcon sx={{ fontSize: 28, color: '#03c75a' }} />,
      url: 'https://cafe.naver.com/iksl0413.cafe?',
      badge: '공식 카페',
      color: '#e6f9ed',
    },
    {
      title: '늘수어 학습 시스템',
      desc: '수어 학습자 문제 풀이 및 실력 평가',
      icon: <SchoolRoundedIcon sx={{ fontSize: 28, color: '#2563eb' }} />,
      url: 'https://neulsign.com',
      badge: '학습 플랫폼',
      color: '#eff6ff',
    },
  ];

  // 사용자 정보 확인 여부 상태 값 파악
  const isInfoConfirmed = (user?.infoConfirmedYn) === 'Y';

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, sm: 3 }, width: '100%', boxSizing: 'border-box' }}>
      {/* 상단 비주얼 배너 */}
      <Card
        elevation={0}
        sx={{
          width: '100%',
          p: { xs: 2.5, sm: 3 },
          mb: { xs: 3, sm: 4 },
          borderRadius: '20px',
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          color: '#fff',
          boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.15)',
          boxSizing: 'border-box',
        }}
      >
        <Box>
          <Chip
            label="KSLTA 공식 웹서비스"
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#93c5fd', fontWeight: 600, mb: 1.5 }}
          />
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800, 
              mb: 1, 
              letterSpacing: '-0.5px',
              fontSize: { xs: '1.5rem', sm: '2.125rem' } // 모바일 폰트 크기 반응형
            }}
          >
            반갑습니다, {user?.name} 선생님! 👋
          </Typography>
          <Typography variant="body1" sx={{ color: '#94a3b8', fontSize: { xs: '0.875rem', sm: '0.98rem' } }}>
            교원 회원님의 정보를 안전하게 확인 및 관리하고, 협회 소식을 확인해 보세요.
          </Typography>
        </Box>
      </Card>

      {/* 외부 주요 서비스 연결 */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#0f172a' }}>
        주요 관련 서비스
      </Typography>
      
      <Stack spacing={2} sx={{ mb: { xs: 3, sm: 4 }, width: '100%' }}>
        {externalLinks.map((link) => (
          <Card
            key={link.title}
            elevation={0}
            component="a"
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              p: 0.5,
              width: '100%',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              textDecoration: 'none', // 카드 전체 링크화 시 텍스트 밑줄 제거
              display: 'block',
              boxSizing: 'border-box',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 12px 20px -5px rgba(0, 0, 0, 0.05)',
                borderColor: '#cbd5e1',
              },
            }}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
                <Avatar sx={{ bgcolor: link.color, width: 48, height: 48, borderRadius: '12px', flexShrink: 0 }}>
                  {link.icon}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                      {link.title}
                    </Typography>
                    <Chip label={link.badge} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {link.desc}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="text"
                sx={{ minWidth: 'auto', p: 1, color: '#64748b', ml: 1, flexShrink: 0 }}
              >
                <OpenInNewRoundedIcon />
              </Button>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* 검증 섹션 */}
      <Stack spacing={2.5} sx={{ width: '100%' }}>
        <Card elevation={0} sx={{ p: { xs: 1, sm: 2 }, borderRadius: '16px', border: '1px solid #e2e8f0', width: '100%', boxSizing: 'border-box' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <VerifiedUserRoundedIcon sx={{ color: '#2563eb' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                내 회원정보 검증 상태
              </Typography>
            </Box>
            <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '12px', mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                자격증 번호: <strong>{user?.certNumber || '미등록'}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                정보 확인 여부:{' '}
                <Chip
                  label={isInfoConfirmed ? '확인 완료' : '미확인'}
                  color={isInfoConfirmed ? 'success' : 'warning'}
                  variant={isInfoConfirmed ? 'filled' : 'outlined'}
                  size="small"
                />
              </Typography>
            </Box>
            <Button 
              fullWidth 
              variant="outlined" 
              onClick={() => onNavigate && onNavigate('profile')}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, py: 1 }}
            >
              내 정보 확인 및 수정하기
            </Button>
          </CardContent>
        </Card>

        {/* 공지사항 카드 섹션 주석 처리 */}
        {/*
        <Card elevation={0} sx={{ p: { xs: 1, sm: 2 }, borderRadius: '16px', border: '1px solid #e2e8f0', width: '100%', boxSizing: 'border-box' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CampaignRoundedIcon sx={{ color: '#f59e0b' }} />
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                  협회 주요 공지
                </Typography>
              </Box>
              <Button size="small" sx={{ color: '#64748b' }}>전체보기</Button>
            </Box>
            <Stack spacing={1.5}>
              {[
                { title: '한국수어교원협회 서비스 신규 오픈 안내', date: '2026.08.20' },
                { title: '수어 교원 자격 정보 시스템 개인정보 업데이트 요청', date: '2026.08.15' },
                { title: '하반기 수어 보수교육 일정 안내', date: '2026.08.01' },
              ].map((notice, i) => (
                <Box
                  key={i}
                  sx={{
                    p: 1.5,
                    borderRadius: '10px',
                    bgcolor: '#f8fafc',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    '&:hover': { bgcolor: '#f1f5f9', cursor: 'pointer' },
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600, 
                      color: '#334155',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {notice.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {notice.date}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
        */}
      </Stack>
    </Box>
  );
};

export default Dashboard;